import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, DollarSign, Clock, BarChart3, ArrowUpRight, ArrowDownRight, Briefcase, Plus } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
import PipelineAnalyticsCard from './PipelineAnalyticsCard';

const DashboardOverview: React.FC = () => {
  const navigate = useNavigate();
  const { stats, projectAnalytics, teamMembers, featuredProject, isLoading, error, fetchOverview } = useDashboardStore();

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage change
  const formatChange = (change?: number): { value: string; trend: 'up' | 'down' } => {
    if (!change) return { value: '0%', trend: 'up' };
    const sign = change >= 0 ? '+' : '';
    return {
      value: `${sign}${change.toFixed(1)}%`,
      trend: change >= 0 ? 'up' : 'down',
    };
  };

  // Format deadline date
  const formatDeadlineDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const deadline = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Prepare stats for display
  const displayStats = stats ? [
    {
      title: 'Total pipeline value',
      value: formatCurrency(stats.totalPipelineValue),
      change: formatChange(stats.pipelineValueChange),
      icon: <TrendingUp className="w-5 h-5 text-white" />,
      isPrimary: true
    },
    {
      title: 'Completed revenue',
      value: formatCurrency(stats.completedRevenue),
      change: formatChange(stats.completedRevenueChange),
      icon: <DollarSign className="w-5 h-5 text-green-600" />,
      iconBg: 'bg-green-100',
      isPrimary: false
    },
    {
      title: 'Pending revenue',
      value: formatCurrency(stats.pendingRevenue),
      change: formatChange(stats.pendingRevenueChange),
      icon: <Clock className="w-5 h-5 text-orange-600" />,
      iconBg: 'bg-orange-100',
      isPrimary: false
    },
    {
      title: 'Active partnerships',
      value: stats.activePartnerships.toString(),
      change: formatChange(stats.activePartnershipsChange),
      icon: <Users className="w-5 h-5 text-primary-600" />,
      iconBg: 'bg-primary-100',
      isPrimary: false
    }
  ] : [];

  // Quick action handlers
  const handleAddPartner = () => {
    navigate('/dashboard/partnerships');
  };

  if (isLoading && !stats) {
    return (
      <div className="h-[133.33vh] flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Plan, prioritize, and accomplish your tasks with ease.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleAddPartner}
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Partnership
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors">
            Import Data
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {displayStats.map((stat, index) => (
          <div
            key={index}
            className={`relative overflow-hidden rounded-2xl p-6 transition-all duration-300 ${stat.isPrimary
              ? 'bg-primary-600 text-white shadow-lg shadow-primary-200 dark:shadow-primary-900/20'
              : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md'
              }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className={`text-sm font-medium ${stat.isPrimary ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'}`}>
                  {stat.title}
                </p>
                {stat.isPrimary && <BarChart3 className="w-5 h-5 text-primary-200 mt-1" />}
              </div>
              {!stat.isPrimary && (
                <div className={`p-2 rounded-lg ${stat.iconBg}`}>
                  {stat.icon}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <h3 className="text-3xl font-bold">{stat.value}</h3>
              <div className={`flex items-center text-sm ${stat.isPrimary
                ? 'text-primary-100'
                : stat.change.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                {stat.change.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                <span className="font-medium">{stat.change.value}</span>
                <span className={`ml-1 ${stat.isPrimary ? 'text-primary-200' : 'text-gray-400'}`}>
                  from last month
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Project Analytics */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Project Analytics</h3>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs font-medium rounded-full">
                  {projectAnalytics.length > 0 ? 'Active' : 'No Data'}
                </span>
              </div>
            </div>

            <div className="h-64 flex items-end justify-between gap-4 px-4">
              {/* Chart Bars */}
              {projectAnalytics.length > 0 ? (
                projectAnalytics.map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-3 flex-1">
                    <div className="w-full relative group">
                      {/* Tooltip for active bar */}
                      {item.active && (
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs py-1 px-2 rounded mb-2 whitespace-nowrap z-10">
                          {item.value}%
                        </div>
                      )}
                      <div
                        className={`w-full rounded-t-xl transition-all duration-300 ${item.active ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                          }`}
                        style={{ height: item.height }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">{item.day}</span>
                  </div>
                ))
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                  No analytics data available
                </div>
              )}
            </div>
          </div>

          {/* Team Collaboration */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Team Collaboration</h3>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <Plus className="w-4 h-4 mr-1.5" />
                Add Member
              </button>
            </div>

            <div className="space-y-4">
              {teamMembers.length > 0 ? (
                teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-4">
                      {member.avatar ? (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                          {member.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{member.name}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{member.currentTask || member.role}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${member.status === 'Online' ? 'bg-green-100 text-green-700' :
                      member.status === 'Away' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                      {member.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No team members found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Pipeline Analytics */}
          <PipelineAnalyticsCard />

          {/* Upcoming Deadlines */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Upcoming Deadlines</h3>
            <div className="space-y-6">
              {[
                { date: 'Nov 28', title: 'Brand Identity Design', due: 'Due in 2 days', color: 'bg-primary-100 text-primary-600' },
                { date: 'Nov 30', title: 'Mobile App Wireframes', due: 'Due in 4 days', color: 'bg-red-100 text-red-600' },
                { date: 'Dec 02', title: 'Website SEO Audit', due: 'Due in 6 days', color: 'bg-gray-100 text-gray-600' }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center text-xs font-bold leading-tight ${item.color}`}>
                    <span>{item.date.split(' ')[0]}</span>
                    <span className="text-sm">{item.date.split(' ')[1]}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{item.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{item.due}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Project</h3>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <Plus className="w-4 h-4 mr-1.5" />
                New
              </button>
            </div>

            {featuredProject ? (
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-100 rounded-xl">
                  <Briefcase className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white">{featuredProject.title}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Due date: {formatDeadlineDate(featuredProject.dueDate)}</p>
                  <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${featuredProject.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                No active projects
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
